import { API_BASE_URL } from '../constants';

class DocumentService {
  /**
   * Download a document as PDF
   * @param {Object} document - Document object with downloadUrl and/or fullText
   * @returns {Promise<void>}
   */
  async downloadPDF(document) {
    try {
      // If there's a direct download URL, fetch and download the original PDF
      if (document.downloadUrl) {
        await this.downloadFromUrl(document.downloadUrl, document.title || 'document.pdf');
        return;
      }

      // If no download URL but we have fullText, generate PDF from text
      if (document.fullText) {
        await this.generatePDFFromText(document);
        return;
      }

      // If neither, try to fetch from backend API
      await this.downloadFromAPI(document.id);

    } catch (error) {
      console.error('PDF download failed:', error);
      throw new Error('Failed to download PDF. The file may not be available.');
    }
  }

  /**
   * Download file from URL
   * @param {string} url - URL to download from
   * @param {string} filename - Desired filename
   */
  async downloadFromUrl(url, filename) {
    try {
      // Fetch the file
      const response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob
      const blob = await response.blob();

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Clean filename
      const cleanFilename = this.cleanFilename(filename);
      link.download = cleanFilename.endsWith('.pdf') ? cleanFilename : `${cleanFilename}.pdf`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

    } catch (error) {
      console.error('Error downloading from URL:', error);
      throw error;
    }
  }

  /**
   * Download PDF from backend API
   * @param {string} documentId - Document ID
   */
  async downloadFromAPI(documentId) {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${documentId}/pdf`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });

      if (!response.ok) {
        throw new Error('PDF not available from API');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `document_${documentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading from API:', error);
      throw error;
    }
  }

  /**
   * Generate PDF from document text
   * @param {Object} document - Document with fullText
   */
  async generatePDFFromText(document) {
    try {
      // Call backend to generate PDF from text
      const response = await fetch(`${API_BASE_URL}/documents/generate-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        body: JSON.stringify({
          title: document.title || 'Document',
          content: document.fullText,
          metadata: {
            authority: document.authority,
            source: document.source,
            publicationDate: document.publicationDate,
            framework: document.primary_regulation || document.framework
          }
        })
      });

      if (!response.ok) {
        throw new Error('PDF generation failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.cleanFilename(document.title || 'document') + '.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error generating PDF:', error);

      // Fallback: Create simple text download
      this.downloadAsText(document);
    }
  }

  /**
   * Fallback: Download as text file
   * @param {Object} document - Document object
   */
  downloadAsText(document) {
    const content = `
${document.title || 'Document'}
${'='.repeat((document.title || 'Document').length)}

Authority: ${document.authority || 'N/A'}
Source: ${document.source || 'N/A'}
Publication Date: ${document.publicationDate || 'N/A'}
Framework: ${document.primary_regulation || document.framework || 'N/A'}

${'-'.repeat(80)}

${document.fullText || document.description || 'No content available.'}

${'-'.repeat(80)}

Downloaded from: Transtar Compliance Terminal
Date: ${new Date().toISOString()}
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.cleanFilename(document.title || 'document') + '.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Clean filename for download
   * @param {string} filename - Raw filename
   * @returns {string} Clean filename
   */
  cleanFilename(filename) {
    return filename
      .replace(/[^a-z0-9_\-\.]/gi, '_')
      .replace(/_{2,}/g, '_')
      .replace(/^_+|_+$/g, '')
      .substring(0, 200);
  }

  /**
   * Check if document has downloadable content
   * @param {Object} document - Document object
   * @returns {boolean}
   */
  isDownloadable(document) {
    return !!(document.downloadUrl || document.fullText);
  }

  /**
   * Get download button label
   * @param {Object} document - Document object
   * @returns {string}
   */
  getDownloadLabel(document) {
    if (document.downloadUrl) {
      return 'Download PDF';
    }
    if (document.fullText) {
      return 'Generate PDF';
    }
    return 'Download';
  }
}

export default new DocumentService();
